/**
 * Structure par défaut du protocole Cockpit ménage.
 *
 * IMPORTANT : les `key` d'action sont des IDENTIFIANTS TECHNIQUES STABLES.
 * Ils sont utilisés :
 *   • en clé de `reservation.cleaning_prep[key]` (case cochée persistée)
 *   • en clé de `cleaning_sessions[bloc.key]` (heures/durées/records)
 *
 * Ne JAMAIS renommer un `key` existant sans migration.
 * Une action ajoutée par l'utilisateur reçoit un nouvel identifiant UUID préfixé `custom_`.
 */

export const DEFAULT_CLEANING_PROTOCOL = {
  blocs: [
    {
      key: "bloc0",
      title: "Bloc 0 — Préparation buanderie",
      subtitle: "Réalisable à l'avance",
      successMsg: "Eva s'est avancée sur la buanderie",
      items: [
        { key: "bloc0_liberer_ligne", label: "Libérer la ligne si nécessaire", hidden: false },
        { key: "bloc0_linge_ligne", label: "Linge sur la ligne", hidden: false },
        { key: "bloc0_paniers", label: "Paniers prêts", hidden: false },
        { key: "bloc0_buanderie_propre", label: "Buanderie propre", hidden: false },
        { key: "bloc0_petit_souffleur", label: "Petit souffleur propre & chargé", hidden: false },
        { key: "bloc0_grand_souffleur", label: "Grand souffleur propre & chargé", hidden: false },
        { key: "bloc0_fer", label: "Fer(s) propres & opérationnels", hidden: false },
        { key: "bloc0_installation_repassage", label: "Installation de repassage prête", hidden: false },
        { key: "bloc0_reserve_eau", label: "Réserve d'eau du fer à proximité", hidden: false },
      ],
    },
    {
      key: "defroissage",
      title: "Bloc ✨ — Défroissage",
      subtitle: "Réalisable à l'avance",
      successMsg: "Eva s'est avancée sur le défroissage",
      items: [
        { key: "defroissage_lit", label: "Linge de lit", hidden: false },
        { key: "defroissage_maison", label: "Linge de maison", hidden: false },
      ],
    },
    {
      key: "b1",
      title: "Bloc 1 — Dégrossissage",
      subtitle: "≈ 30 min",
      items: [
        { key: "b1_ouvrir_aerer", label: "Tout ouvrir / aérer", hidden: false },
        { key: "b1_vider_bain", label: "Vider le bain", hidden: false },
        { key: "b1_souffler_linge_lit", label: "Souffler le linge de lit", hidden: false },
        { key: "b1_retirer_affaires", label: "Retirer les affaires voyageurs", hidden: false },
        { key: "b1_retirer_decorations", label: "Retirer les décorations si nécessaire", hidden: false },
        { key: "b1_retirer_poubelles", label: "Retirer les poubelles", hidden: false },
        { key: "b1_bougies_recharger", label: "Mettre les bougies à recharger", hidden: false },
        { key: "b1_sortir_diatomee", label: "Sortir les plaques/tapis de diatomée (ne pas nettoyer)", tip: "Uniquement les sortir à cette étape — le nettoyage vient en Bloc 2.", hidden: false },
        { key: "b1_petit_souffleur", label: "Petit souffleur", hidden: false },
        { key: "b1_grand_souffleur", label: "Grand souffleur général", hidden: false },
        { key: "b1_detachant", label: "Détachant si nécessaire", hidden: false },
        { key: "b1_linge_machine", label: "Mettre le linge de lit en machine", hidden: false },
      ],
    },
    {
      key: "b2",
      title: "Bloc 2 — Nettoyage complet + sols + bain",
      subtitle: "≈ 45 min",
      items: [
        { key: "b2_sanitol_surfaces", label: "Sanitol / vinaigre & surfaces", hidden: false },
        { key: "b2_poignees", label: "Poignées", hidden: false },
        { key: "b2_interrupteurs", label: "Interrupteurs", hidden: false },
        { key: "b2_zones_contact", label: "Zones de contact", hidden: false },
        { key: "b2_lavabo", label: "Lavabo", hidden: false },
        { key: "b2_douche", label: "Douche", hidden: false },
        { key: "b2_wc", label: "WC", hidden: false },
        { key: "b2_siphon", label: "Siphon", hidden: false },
        { key: "b2_surfaces_chambre", label: "Surfaces & dépoussiérage chambre", hidden: false },
        { key: "b2_miroir", label: "Miroir", hidden: false },
        { key: "b2_aspiration", label: "Aspiration complète", hidden: false },
        { key: "b2_serpilliere", label: "Serpillière complète", hidden: false },
        { key: "b2_terrasse", label: "Terrasse", hidden: false },
        { key: "b2_dalle_ext", label: "Dalle extérieure", hidden: false },
        { key: "b2_bain", label: "Nettoyage du bain", hidden: false },
        { key: "b2_diatomee_rincage", label: "Nettoyage / rinçage des plaques de diatomée", hidden: false },
      ],
    },
    {
      key: "lit",
      title: "Réfection du lit",
      subtitle: "≈ 13 à 15 min",
      banner: "Prends tes chaussettes et une serpillière pour essuyer.",
      items: [
        { key: "lit_preparer_linge", label: "Préparer le linge déjà défroissé", hidden: false },
        { key: "lit_refaire", label: "Refaire complètement le lit", hidden: false },
        { key: "lit_taies", label: "Taies / oreillers", hidden: false },
        { key: "lit_couette", label: "Couette", hidden: false },
        { key: "lit_presentation", label: "Présentation", hidden: false },
        { key: "lit_antipoils", label: "Anti-poils si nécessaire", hidden: false },
        { key: "lit_controle", label: "Contrôle visuel final", hidden: false },
      ],
    },
    {
      key: "finitions",
      title: "Finitions chambre + salle de bain",
      subtitle: "≈ 15 min",
      items: [
        { key: "fin_rideaux", label: "Remettre les rideaux de la chambre", hidden: false },
        { key: "fin_bougies_place", label: "Remettre les bougies en place", hidden: false },
        { key: "fin_piles", label: "Changer les piles si nécessaire", hidden: false },
        { key: "fin_porte_vitree_check", label: "Vérifier la porte vitrée d'entrée", hidden: false },
        { key: "fin_porte_vitree_nettoyer", label: "Nettoyer la porte vitrée si nécessaire", hidden: false },
        { key: "fin_ecran_miroir", label: "Vérifier / nettoyer écran & miroir", hidden: false },
        { key: "fin_serviettes_recup", label: "Récupérer les 3 serviettes de salle de bain", hidden: false },
        { key: "fin_serviettes_place", label: "Mettre les serviettes en place", hidden: false },
        { key: "fin_diatomee_recup", label: "Récupérer les plaques de diatomée", hidden: false },
        { key: "fin_diatomee_place", label: "Remettre les plaques à leur place", hidden: false },
        { key: "fin_essuyage_sdb", label: "Check essuyage salle de bain", hidden: false },
        { key: "fin_fermer_sdb", label: "Fermer la salle de bain", hidden: false },
      ],
    },
    {
      key: "cuisine",
      title: "Cuisine",
      subtitle: "≈ 30 min",
      items: [
        { key: "cuis_ranger", label: "Ranger", hidden: false },
        { key: "cuis_chiffon", label: "Nettoyage au chiffon mouillé", hidden: false },
      ],
    },
    {
      key: "dr",
      title: "✨ Dernier regard",
      subtitle: "≈ 30 min",
      items: [
        { key: "dr_table", label: "Aménager / dresser la table", hidden: false },
        { key: "dr_check_pieces", label: "Check rapide de chaque pièce", hidden: false },
        { key: "dr_souffleur", label: "Petit coup de souffleur uniquement si besoin", hidden: false },
        { key: "dr_senteur", label: "Sent-bon / senteur dans chaque pièce", hidden: false },
        { key: "dr_visuel", label: "Contrôle visuel final du Refuge", hidden: false },
      ],
    },
  ],
};

/**
 * Fusionne un protocole côté serveur (potentiellement partiel) avec les défauts
 * pour garantir la présence de toutes les propriétés de rendu (banner, tip, subtitle).
 */
export function mergeProtocolWithDefaults(serverProtocol) {
  if (!serverProtocol || !Array.isArray(serverProtocol.blocs)) {
    return DEFAULT_CLEANING_PROTOCOL;
  }
  const defaultBlocs = new Map(DEFAULT_CLEANING_PROTOCOL.blocs.map((b) => [b.key, b]));
  const defaultItems = new Map();
  DEFAULT_CLEANING_PROTOCOL.blocs.forEach((b) => {
    (b.items || []).forEach((i) => defaultItems.set(i.key, i));
  });
  const blocs = serverProtocol.blocs.map((b) => {
    const d = defaultBlocs.get(b.key) || {};
    const items = (b.items || []).map((it) => {
      const di = defaultItems.get(it.key) || {};
      return {
        key: it.key,
        label: it.label ?? di.label ?? "",
        tip: it.tip ?? di.tip,
        hidden: !!it.hidden,
      };
    });
    return {
      key: b.key,
      title: b.title ?? d.title ?? "",
      subtitle: b.subtitle ?? d.subtitle,
      banner: b.banner ?? d.banner,
      successMsg: b.successMsg ?? d.successMsg,
      items,
    };
  });
  return { blocs };
}
